/**
 * Verbatim Shopify line items, shared by the two surfaces that render them.
 *
 * The order detail reads `OrderSummary.line_items` (an ARRAY on the wire) and
 * the delivery job reads `FulfillmentJob.line_items_json` (a STRING). Both
 * descend from the same stored `raw_line_items` blob and hold the same ELEMENT
 * shape, so the title fallback and the product-id normalisation live here once
 * rather than twice — a widget module may not import another feature's, and
 * copying them is how the two tables would start to disagree about what a
 * product id looks like.
 *
 * The DOORS stay separate, and that asymmetry is load-bearing rather than an
 * oversight: see `parseLineItemsJson` for why the order surface keeps its own.
 *
 * Pure and transport-free on purpose: `src/__tests__/architecture/
 * widget-modules-transport-free.test.ts` polices what a widget may reach, and
 * every widget in both features reaches this.
 */

/**
 * One verbatim Shopify line item — only the members the two tables render.
 * Everything is optional and defensively typed: this is raw Shopify JSON that
 * has passed through several ingest doors over the years, not a contract.
 */
export interface LineItem {
  title?: string;
  name?: string;
  quantity?: number;
  price?: string | number;
  product_id?: string | number;
}

/**
 * Parse a line-items JSON STRING into an array — the delivery job's wire shape
 * (`FulfillmentJob.line_items_json`).
 *
 * A parse failure REPORTS (`console.warn`, read by whoever has devtools
 * open) and then
 * degrades to `[]`: a bad blob is a data fault and the field must still
 * render, but swallowing it silently is what CLAUDE.md §3 forbids. Null /
 * undefined / `''` return `[]` with NO warning — a job whose order carried
 * nothing for its delivery date is the everyday case, not a fault (#1896).
 *
 * This is deliberately NOT shared with the order surface, whose `line_items`
 * arrives as a verbatim ARRAY and whose own `parseLineItems` warns on a string
 * BY DESIGN: #1409 removed the string shape from that wire, so a string
 * reaching it means something upstream re-serialised, and folding the two
 * helpers together would quietly undo that guard. What the two surfaces share
 * is the element shape below, not the door.
 */
export function parseLineItemsJson(value: unknown): LineItem[] {
  if (value == null || value === '') return [];
  if (Array.isArray(value)) return value as LineItem[];
  if (typeof value !== 'string') {
    console.warn('[line-items] line_items_json is neither a string nor an array', value);
    return [];
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch (error) {
    console.warn('[line-items] line_items_json is not valid JSON', error);
    return [];
  }
  if (!Array.isArray(parsed)) {
    console.warn('[line-items] line_items_json did not parse to an array', parsed);
    return [];
  }
  return parsed as LineItem[];
}

/**
 * Is this array entry a line-item object? A job with a 配送单清单 passes the
 * order's raw entries through verbatim (ADR-0046 decision 6), so `null`, a
 * string or a nested array can sit beside the real lines. Such an entry names
 * no product and carries no quantity: a table has no row to draw for it.
 */
export function isLineItemRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Trimmed text, or '' when the raw member is not a string. */
function textOf(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * The operator-facing product name, or null when the line names none. The
 * members are read type-safely: raw JSON may hold a non-string `title`.
 */
export function lineItemTitle(item: LineItem): string | null {
  return textOf(item.title) || textOf(item.name) || null;
}

/**
 * The numeric Shopify product key, or null.
 *
 * The stored blob carries whichever shape its ingest door produced — a REST
 * number, a GraphQL numeric string, or a GID (`gid://shopify/Product/123`) —
 * and `products.shopify_id`, which is what `/products/:id` addresses, holds
 * the bare numeric text. This is the frontend twin of the backend's
 * `normalizeProductId` (`backend/src/contexts/delivery/app/task-details.ts`),
 * which normalises the same three shapes at its own parse boundary; the two
 * agree because they must resolve to the same key for the same item.
 */
export function lineItemProductId(item: LineItem): string | null {
  const raw = item.product_id;
  if (raw == null) return null;
  const text = String(raw).trim();
  if (text === '') return null;
  if (text.startsWith('gid://')) {
    const match = text.match(/\/(\d+)$/);
    return match ? match[1]! : null;
  }
  return text;
}

/** The admin's own product record for this line, or null when it names none. */
export function lineItemProductPath(item: LineItem): string | null {
  const id = lineItemProductId(item);
  return id === null ? null : `/products/${id}`;
}
